// item-search-history.ts — side-info-panel item-search Back/Forward navigation, carved out of template.js
// (roadmap T4.3, 2026-06-16). Byte-faithful port: the only edits versus the template.js originals are the
// `: any` param annotations and the `any`-typed DOM-element locals in updateNavigation — logic is identical.
//
// ⚠ The history STATE (searchHistoryItemSearches / currentItemSearchesIndex) is NOT carved — it STAYS in
// template.js (shared with the staying openItemInfo / refreshSidePanel). These functions read AND write it as
// bare global-lexical bindings; template.js executes first, so the writes hit template.js's declarations. See
// globals.d.ts. The bottom of this module self-runs updateNavigation() once — the page-load button-state init
// that template.js used to do at top-level (its call is now a tombstone).

function addToSearchHistory(item_id: string): void {
    // A brand-new search starts a fresh timeline, so any remembered rune symbol for the
    // old "forward" slot no longer applies — drop it so Forward doesn't resurrect a stale
    // overlay against an unrelated entry.
    window.__ftSideInfoRunePendingForwardIndex = null;
    window.__ftSideInfoRuneOverlayIr = null;
    // Re-opening the entry we are already parked on (holding the same action-bar slot twice, clicking
    // the same reference again) is a re-render, not a new step: pushing a duplicate made the next
    // Back look like a no-op (owner, 2026-09-01). The overlay/return resets above still apply.
    const current = searchHistoryItemSearches[currentItemSearchesIndex];
    const isSameEntry = typeof current === 'string' && current.toUpperCase() === String(item_id).toUpperCase();
    if (!isSameEntry) {
        // Truncate history beyond current index
        searchHistoryItemSearches = searchHistoryItemSearches.slice(0, currentItemSearchesIndex + 1);
        searchHistoryItemSearches.push(item_id);
        currentItemSearchesIndex = searchHistoryItemSearches.length - 1;
    }
    sessionStorage.setItem('searchHistoryItemSearches', JSON.stringify(searchHistoryItemSearches));
    sessionStorage.setItem('currentItemSearchesIndex', String(currentItemSearchesIndex));
    // Breakdown drill-in: ftDrillFromBreakdown() (active-panel.ts) sets __ftBreakdownDrillArming
    // right before this open, having already snapshotted the breakdown. Remember the index the
    // item just landed on so the first Back from it restores the breakdown (see goPrevious…). Any
    // other (non-drill) search invalidates a stale return so Back doesn't resurrect an old panel.
    if (window.__ftBreakdownDrillArming) {
        window.__ftBreakdownReturnIndex = currentItemSearchesIndex;
        window.__ftBreakdownReturnActive = false;
        window.__ftBreakdownDrillArming = false;
    } else {
        window.__ftBreakdownReturnActive = false;
        window.__ftBreakdownReturnIndex = null;
        if (typeof window.ftClearBreakdownReturn === 'function') window.ftClearBreakdownReturn();
    }
    updateNavigation();
}

// Go to previous item
async function goPreviousItemSearches(): Promise<void> {
    // Inventory weight breakdown drill: while the pie is on screen and nested, the side-info Back arrow
    // walks UP one drill level (and the Forward arrow re-descends) before touching item-search history —
    // same "intercept Back/Forward for an in-panel overlay" model as the rune preview below.
    if (typeof ftInvWeightCanGoUp === 'function' && ftInvWeightCanGoUp()) {
        ftInvWeightGoUp();
        updateNavigation();
        return;
    }
    // If a transient rune-preview overlay is showing (openRuneInfo wrote into the panel
    // without pushing a history entry), the first Back pops the overlay and restores the
    // current history entry — the ability/spell the rune belongs to — instead of skipping
    // it to navigate to the previous item.
    if (window.__ftSideInfoRuneOverlayActive) {
        window.__ftSideInfoRuneOverlayActive = false;
        // Remember that Forward, while still parked on this history entry, should re-open
        // the rune symbol (kept in __ftSideInfoRuneOverlayIr) rather than walk history.
        window.__ftSideInfoRunePendingForwardIndex = currentItemSearchesIndex;
        const item_id = searchHistoryItemSearches[currentItemSearchesIndex];
        if (item_id) {
            await openItemInfo(item_id, false); // re-render current entry, do not add to search
        }
        updateNavigation();
        return;
    }
    // Breakdown drill-in return (off-history view): the FIRST Back from an item we drilled into
    // FROM a breakdown panel restores that breakdown in place (without walking the item stack); a
    // SECOND Back then leaves it and continues to the previous searched item. Sits AFTER the rune
    // overlay (which is the more-transient layer): a rune preview pops first, then the breakdown.
    // State is set in addToSearchHistory() when ftDrillFromBreakdown() armed it.
    if (
        !window.__ftBreakdownReturnActive
        && typeof window.ftHasBreakdownReturn === 'function' && window.ftHasBreakdownReturn()
        && window.__ftBreakdownReturnIndex === currentItemSearchesIndex
    ) {
        if (typeof window.ftRestoreBreakdownReturn === 'function' && window.ftRestoreBreakdownReturn()) {
            window.__ftBreakdownReturnActive = true; // now showing the breakdown; Forward re-opens the item
            updateNavigation();
            return;
        }
    }
    if (window.__ftBreakdownReturnActive) {
        // Back again while the restored breakdown is showing → drop it and walk item history normally.
        window.__ftBreakdownReturnActive = false;
        if (typeof window.ftClearBreakdownReturn === 'function') window.ftClearBreakdownReturn();
    }
    if (currentItemSearchesIndex > 0) {
        currentItemSearchesIndex--;
        sessionStorage.setItem('currentItemSearchesIndex', String(currentItemSearchesIndex));
        // The Workspace header owns these controls now. Reflect the new timeline position before
        // awaiting a cache/server render so the arrows never advertise the stale direction or
        // accept a second click against the old index while the destination is loading.
        updateNavigation();
        const item_id = searchHistoryItemSearches[currentItemSearchesIndex];
        if (item_id) await openItemInfo(item_id, false); // do not add to search
        updateNavigation();
    }
}

// Go to next item
async function goForwardItemSearches(): Promise<void> {
    // Inventory weight breakdown drill: re-descend into the level the user backed out of (mirror of the
    // Back interception above) before advancing item-search history.
    if (typeof ftInvWeightCanGoForward === 'function' && ftInvWeightCanGoForward()) {
        ftInvWeightGoForward();
        updateNavigation();
        return;
    }
    // Breakdown drill-in return: while the restored breakdown is showing (Back put us here), the
    // first Forward re-opens the item we drilled into — re-arming Back ⇄ Forward for the pair.
    if (
        window.__ftBreakdownReturnActive
        && window.__ftBreakdownReturnIndex === currentItemSearchesIndex
    ) {
        window.__ftBreakdownReturnActive = false;
        const item_id = searchHistoryItemSearches[currentItemSearchesIndex];
        if (item_id) {
            await openItemInfo(item_id, false); // re-render the drilled item, do not add to search
        }
        updateNavigation();
        return;
    }
    // If we backed out of a rune overlay and are still parked on the same history entry,
    // the first Forward re-opens that rune symbol (round-tripping Back ⇄ Forward for the
    // preview) instead of advancing to the next searched item.
    if (
        !window.__ftSideInfoRuneOverlayActive
        && window.__ftSideInfoRunePendingForwardIndex != null
        && window.__ftSideInfoRunePendingForwardIndex === currentItemSearchesIndex
        && window.__ftSideInfoRuneOverlayIr
        && typeof openRuneInfo === 'function'
    ) {
        openRuneInfo(window.__ftSideInfoRuneOverlayIr); // re-shows overlay + updateNavigation
        return;
    }
    if (currentItemSearchesIndex < searchHistoryItemSearches.length - 1) {
        currentItemSearchesIndex++;
        sessionStorage.setItem('currentItemSearchesIndex', String(currentItemSearchesIndex));
        // Keep the persistent Workspace chrome synchronized immediately; openItemInfo may need a
        // network fallback before it can repaint the destination content.
        updateNavigation();
        const item_id = searchHistoryItemSearches[currentItemSearchesIndex];
        if (item_id) await openItemInfo(item_id, false); // do not add to search
        updateNavigation();
    }
}

// Enable/disable navigation buttons
function updateNavigation(): void {
    const btnPrevious = document.getElementById('btn_previous_item_searched') as HTMLButtonElement;
    const btnNext = document.getElementById('btn_next_item_searched') as HTMLButtonElement;
    // Rune overlay nav model (linear): [prev item] ⇄ [ability/item] ⇄ [rune symbol].
    // While the overlay is showing, Back pops it (always available, even at index 0) and
    // Forward has nowhere further to go. After backing out, Forward can re-open the rune
    // symbol while we're still parked on the entry it overlays.
    const runeOverlay = !!window.__ftSideInfoRuneOverlayActive;
    const runeForwardAvailable =
        !runeOverlay
        && window.__ftSideInfoRunePendingForwardIndex != null
        && window.__ftSideInfoRunePendingForwardIndex === currentItemSearchesIndex
        && !!window.__ftSideInfoRuneOverlayIr;
    // Inventory weight breakdown drill state — keeps the arrows live while nesting in/out of the pie.
    const invBack = typeof ftInvWeightCanGoUp === 'function' && ftInvWeightCanGoUp();
    const invForward = typeof ftInvWeightCanGoForward === 'function' && ftInvWeightCanGoForward();
    // Breakdown drill-in state: bdActive = the restored breakdown is showing (Forward re-opens the
    // drilled item); bdReturnAvail = we're on the drilled item and Back can restore the breakdown
    // (force-enabled even at index 0, since the breakdown is off-history and may have no item before it).
    const bdActive = !!window.__ftBreakdownReturnActive;
    const bdReturnAvail =
        !bdActive
        && typeof window.ftHasBreakdownReturn === 'function' && window.ftHasBreakdownReturn()
        && window.__ftBreakdownReturnIndex === currentItemSearchesIndex;
    btnPrevious.disabled = !invBack && !runeOverlay && !bdReturnAvail && currentItemSearchesIndex <= 0;
    btnNext.disabled = invForward
        ? false
        : runeOverlay
            ? true
            : bdActive
                ? false
                : !runeForwardAvailable && currentItemSearchesIndex >= searchHistoryItemSearches.length - 1;
}

// ===========================================================================================
// Window attachment — the public surface the original top-level functions exposed.
// goPreviousItemSearches / goForwardItemSearches: PHP onclick in includes/sidepanels/sideinfo.php.
// updateNavigation: PHP/side-panels-init.js/inventory-breakdown typeof-guarded callers + the page-load init.
// addToSearchHistory: the staying openItemInfo path + source.php typeof-guarded call. All reached as bare
// globals at runtime.
// ===========================================================================================
declare global {
    interface Window {
        addToSearchHistory: typeof addToSearchHistory;
        goPreviousItemSearches: typeof goPreviousItemSearches;
        goForwardItemSearches: typeof goForwardItemSearches;
        updateNavigation: typeof updateNavigation;
    }
}

Object.assign(window, {
    addToSearchHistory,
    goPreviousItemSearches,
    goForwardItemSearches,
    updateNavigation,
});

// Page-load init — relocated from template.js's removed top-level `updateNavigation();` call. Runs once on
// bundle load (after template.js, so the history STATE + the side-info buttons exist), byte-faithfully setting
// the Back/Forward button state. Matches the original unguarded parse-time behavior.
updateNavigation();

export {};
